// Copy Button Component

import React, { useState } from 'react';
import { Button } from './Button';

interface CopyButtonProps {
  text: string;
  onCopy?: (text: string) => void;
  onError?: (error: Error) => void;
  variant?:
    | 'default'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'destructive'
    | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
  showIcon?: boolean;
  copyLabel?: string;
  copiedLabel?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  onCopy,
  onError,
  variant = 'outline',
  size = 'default',
  className = '',
  children,
  showIcon = true,
  copyLabel = 'Copy',
  copiedLabel = 'Copied!',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy?.(text);

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);

      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        setCopied(true);
        onCopy?.(text);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
        onError?.(err as Error);
      }
    }
  };

  const buttonContent = children || (
    <>
      {showIcon && <span className="mr-2">{copied ? '✅' : '📋'}</span>}
      {copied ? copiedLabel : copyLabel}
    </>
  );

  return (
    <Button
      onClick={handleCopy}
      variant={copied ? 'default' : variant}
      size={size}
      className={className}
    >
      {buttonContent}
    </Button>
  );
};
